import type { DialogOptions } from '@/shared/stores/dialog';

export function resolveRegisterAppointmentDate(jsonData: Record<string, unknown>): string {
    return String(jsonData.APPOINTMENT_DATE ?? jsonData.DOAPP ?? '').trim();
}

export function hasRegisterAppointmentDate(jsonData: Record<string, unknown>): boolean {
    return resolveRegisterAppointmentDate(jsonData).length > 0;
}

type TranslateFn = (key: string) => string;

function readErrorCode(err: unknown): string {
    const e = err as Record<string, unknown> | undefined;
    const response = e?.response as Record<string, unknown> | undefined;
    const data = response?.data as Record<string, unknown> | undefined;
    const error = data?.error as Record<string, unknown> | undefined;
    return String(error?.code ?? '').trim();
}

function readErrorMessage(err: unknown): string {
    const e = err as Record<string, unknown> | undefined;
    const response = e?.response as Record<string, unknown> | undefined;
    const data = response?.data as Record<string, unknown> | undefined;
    const error = data?.error as Record<string, unknown> | undefined;
    return String(
        error?.message
        ?? data?.message
        ?? e?.message
        ?? '',
    ).trim();
}

export function hasRegisterContactIdentity(jsonData: Record<string, unknown>): boolean {
    const contactCd = String(jsonData.CONTACT_CD ?? '').trim();
    if (contactCd) return true;
    return String(jsonData.CONTACT_NAME ?? jsonData.ENTITY_NAME ?? '').trim().length > 0;
}

export function translateRegisterAddErrorMessage(t: TranslateFn, err: unknown, fallback?: string): string {
    if (err instanceof RegisterAddValidationError) {
        if (err.code === 'APPOINTMENT_REQUIRED') {
            return t('register.detail.appointmentRequired');
        }
        if (err.code === 'CONTACT_NAME_REQUIRED') {
            return t('register.detail.contactNameRequired') || 'Name is required.';
        }
    }

    const code = readErrorCode(err);
    const raw = readErrorMessage(err);

    if (code === 'OFFICER007' || raw.includes('이미 해당 직책')) {
        return t('register.detail.officerAlreadyExists') || t('entity.Officer with this position already exists');
    }
    if (code === 'REGMBR001' || raw === 'MEMBER_ALREADY_EXISTS') {
        return t('register.detail.memberAlreadyExists');
    }
    if (code === 'CTRL008' || raw === 'BENEFICIAL_OWNER_ALREADY_EXISTS') {
        return t('register.detail.beneficialOwnerAlreadyExists');
    }
    if (raw === 'APPOINTMENT_REQUIRED') {
        return t('register.detail.appointmentRequired');
    }

    return raw || fallback || t('register.detail.saveFailed');
}

export async function alertRegisterAppointmentRequired(
    alertFn: (options: DialogOptions | string) => Promise<void>,
    t: TranslateFn,
): Promise<void> {
    await alertFn({
        title: t('register.detail.error') || 'Error',
        message: t('register.detail.appointmentRequired'),
        type: 'error',
    });
}

export async function validateRegisterOfficerSaveFields(
    jsonData: Record<string, unknown>,
    alertFn: (options: DialogOptions | string) => Promise<void>,
    t: TranslateFn,
): Promise<boolean> {
    if (!hasRegisterContactIdentity(jsonData)) {
        await alertFn({
            title: t('register.detail.error') || 'Error',
            message: t('register.detail.contactNameRequired') || 'Name is required.',
            type: 'error',
        });
        return false;
    }
    if (!hasRegisterAppointmentDate(jsonData)) {
        await alertRegisterAppointmentRequired(alertFn, t);
        return false;
    }
    return true;
}

/** @deprecated validateRegisterOfficerSaveFields */
export const validateRegisterAddFields = validateRegisterOfficerSaveFields;

export class RegisterAddValidationError extends Error {
    readonly code: string;

    constructor(code: string) {
        super(code);
        this.name = 'RegisterAddValidationError';
        this.code = code;
    }
}

export function assertRegisterAppointmentDate(jsonData: Record<string, unknown>): void {
    if (!hasRegisterAppointmentDate(jsonData)) {
        throw new RegisterAddValidationError('APPOINTMENT_REQUIRED');
    }
}
